import { type CSSProperties } from "react";

import {
  createTheme,
  responsiveFontSizes,
  type Theme
} from "@mui/material/styles";

import { components } from "@/src/theme/customizations/components";
import {
  breakpoints,
  createHeadingBreakpointTypography,
  darkPalette,
  lightPalette,
  spacingUnit,
  typography
} from "@/src/theme/tokens";
import type { ResolvedSemanticColor } from "@/src/theme/tokens";

type CustomTypographyVariant =
  | "buttonLarge"
  | "buttonMedium"
  | "buttonSmall"
  | "subtitle3"
  | "avatarLetter"
  | "inputLabel"
  | "helperText"
  | "inputText"
  | "tooltip"
  | "link"
  | "linkSmall";

type CustomTypographyVariants = Record<CustomTypographyVariant, CSSProperties>;

declare module "@mui/material/styles" {
  interface TypographyVariants extends CustomTypographyVariants {
    fontWeightSemibold: number;
    fontWeightExtrabold: number;
  }

  interface TypographyVariantsOptions extends Partial<CustomTypographyVariants> {
    fontWeightSemibold?: number;
    fontWeightExtrabold?: number;
  }

  interface Typography {
    fontWeightSemibold: number;
    fontWeightExtrabold: number;
  }

  interface TypographyOptions {
    fontWeightSemibold?: number;
    fontWeightExtrabold?: number;
  }

  interface Palette {
    brand: {
      secondary: string;
      tertiary: string;
      katsastus: string;
    };
    misc: {
      coldBackground: string;
      outlineBorder: string;
      standardInputLine: string;
      backdropOverlay: string;
      ratingActive: string;
      timelineBackground: string;
      snackbarBackground: string;
      almaLoginMain?: string;
      almaLoginHover?: string;
      almaLoginFocusRing?: string;
      elevation1?: string;
      elevation2?: string;
      elevation3?: string;
      elevation4?: string;
      elevation6?: string;
      elevation8?: string;
      elevation12?: string;
      elevation16?: string;
      elevation24?: string;
    };
    semantic: {
      primary: ResolvedSemanticColor;
      secondary: ResolvedSemanticColor;
      error: ResolvedSemanticColor;
      warning: ResolvedSemanticColor;
      info: ResolvedSemanticColor;
      success: ResolvedSemanticColor;
    };
  }

  interface PaletteOptions {
    brand?: {
      secondary?: string;
      tertiary?: string;
      katsastus?: string;
    };
    misc?: Partial<Palette["misc"]>;
    semantic?: Partial<Palette["semantic"]>;
  }
}

declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides extends Record<
    CustomTypographyVariant,
    true
  > {}
}

const baseThemeOptions = {
  breakpoints,
  spacing: spacingUnit,
  typography,
  components
};

/** MUI auto-scales these variants; h1–h3 use explicit Figma breakpoint tokens. */
const responsiveFontSizeVariants = [
  "h4",
  "h5",
  "h6",
  "subtitle1",
  "subtitle2",
  "body1",
  "body2"
] as const;

const createThemeWithResponsiveTypography = (
  themeOptions: Parameters<typeof createTheme>[0]
) => {
  const baseTheme = createTheme(themeOptions);
  const themeWithHeadingBreakpoints = createTheme(baseTheme, {
    typography: createHeadingBreakpointTypography(baseTheme)
  });

  return responsiveFontSizes(themeWithHeadingBreakpoints, {
    variants: [...responsiveFontSizeVariants]
  });
};

/** Light-mode theme (default). Kept as a named export for direct imports. */
export const lightTheme: Theme = createThemeWithResponsiveTypography({
  ...baseThemeOptions,
  palette: lightPalette
});

/** Dark-mode theme from the Baana C2B Figma design system. */
export const darkTheme: Theme = createThemeWithResponsiveTypography({
  ...baseThemeOptions,
  palette: darkPalette
});

/**
 * App theme with light and dark color schemes.
 * Use `defaultMode="light"` on ThemeProvider to avoid unintended dark mode.
 */
export const theme: Theme = createThemeWithResponsiveTypography({
  ...baseThemeOptions,
  colorSchemes: {
    light: {
      palette: lightPalette
    },
    dark: {
      palette: darkPalette
    }
  }
});

export type AppTheme = Theme;
